import type { UseFormReturn } from 'react-hook-form';
import { z } from 'zod/v3';

import type { OAISchema } from '~/types/openapi';

import {
  annualRevenueSchema,
  businessOwnerTypeSchema,
  inquiryTypeSchema,
  quoteTypeSchema,
  requesterIndustrySchema,
  serviceTypeSchema,
} from './schemas';

// ============================================================================
// 견적 요청 폼 데이터 스키마 (일반 견적: COMPARE, ONE_TO_ONE)
// ============================================================================

/**
 * 홈택스 연동 카드 여부
 * @description 폼에서 사용하는 Y/N 선택값
 * - Y: 있음
 * - N: 없음
 */
export const hometaxCardSchema = z.enum(['Y', 'N']);
export type HometaxCard = z.infer<typeof hometaxCardSchema>;

/**
 * 견적 요청 폼 데이터 스키마
 * @description 세무이지 일반 견적 요청 폼에서 사용하는 데이터 (COMPARE, ONE_TO_ONE)
 * @see POST /api/market-place/partners/quote-requests
 */
export const registerFormDataSchema = z.object({
  /** 연 매출 구간 (필수) - UNDER_50M, UNDER_100M, UNDER_300M 등 */
  annualRevenue: annualRevenueSchema.or(z.literal('')),

  /** 사업장 주소 (선택) */
  businessAddress: z.string(),

  /** 사업장 상세주소 (선택) */
  businessDetailAddress: z.string(),

  /** 사업자 유형: INDIVIDUAL_GENERAL / INDIVIDUAL_SIMPLIFIED / CORPORATION / OTHER (필수) */
  businessOwnerType: businessOwnerTypeSchema.or(z.literal('')),

  /** 사업장 지역 코드 (필수) - 주소 검색 시 자동 설정 */
  businessRegionCode: z.string(),

  /** 사업장 우편번호 (선택) - 주소 검색 시 자동 설정 */
  businessZipCode: z.string(),

  /** 홈택스 연동 신용카드 여부 (필수) - Y: 있음, N: 없음 */
  hasHometaxCard: hometaxCardSchema.or(z.literal('')),

  /** 문의 유형 (필수) - BOOKKEEPING, VAT_FILING, INCOME_TAX 등 */
  inquiryType: inquiryTypeSchema.or(z.literal('')),

  /** 견적 유형: COMPARE(비교견적) / ONE_TO_ONE(1:1문의) (필수) */
  quoteType: quoteTypeSchema,

  /** 요청 내용 (선택) - 추가 요청사항 */
  requestContent: z.string(),

  /** 요청자 업종 (필수) - RESTAURANT, TRADE, PROFESSIONAL_SERVICE 등 */
  requesterIndustry: requesterIndustrySchema.or(z.literal('')),

  /** 요청자 이름 (필수) */
  requesterName: z.string(),

  /** 요청자 연락처 (필수) */
  requesterPhone: z.string(),

  /** 서비스 유형 (필수) - 현재는 TAX만 지원 */
  serviceType: serviceTypeSchema,

  /** 대상 파트너 ID 목록 (필수) - 견적 요청할 세무사 ID 배열 */
  targetPartnerIds: z.array(z.number()),
});
export type RegisterFormData = z.infer<typeof registerFormDataSchema>;

/**
 * 폼 기본값
 * @description useForm의 defaultValues로 사용
 */
export const registerFormDefaultValues: RegisterFormData = {
  // Step 1 (업종, 사업자유형, 문의사항)
  requesterIndustry: '',
  businessOwnerType: '',
  inquiryType: '',
  // Step 2 (주소, 매출규모)
  annualRevenue: '',
  businessAddress: '',
  businessDetailAddress: '',
  businessRegionCode: '',
  businessZipCode: '',
  // Step 3 (비용처리)
  hasHometaxCard: 'Y',
  requestContent: '',
  // Step 4 (연락처)
  requesterName: '',
  requesterPhone: '',
  // 기타
  quoteType: 'COMPARE',
  serviceType: 'TAX',
  targetPartnerIds: [],
};

/**
 * RegisterFormData를 API 요청 형식으로 변환
 * @description 폼 데이터를 CreateQuoteRequestRequest로 변환
 */
export function toCreateQuoteRequest(
  formData: RegisterFormData,
): OAISchema['CreateQuoteRequestRequest'] {
  return {
    annualRevenue: formData.annualRevenue,
    businessAddress: formData.businessAddress || undefined,
    businessDetailAddress: formData.businessDetailAddress || undefined,
    businessOwnerType:
      formData.businessOwnerType as OAISchema['CreateQuoteRequestRequest']['businessOwnerType'],
    businessRegionCode: formData.businessRegionCode,
    businessZipCode: formData.businessZipCode || undefined,
    inquiryType:
      formData.inquiryType as OAISchema['CreateQuoteRequestRequest']['inquiryType'],
    quoteType: formData.quoteType,
    requestContent: formData.requestContent || undefined,
    requesterIndustry:
      formData.requesterIndustry as OAISchema['CreateQuoteRequestRequest']['requesterIndustry'],
    requesterName: formData.requesterName,
    requesterPhone: formData.requesterPhone,
    serviceType: formData.serviceType,
    targetPartnerIds: formData.targetPartnerIds,
  };
}

// ============================================================================
// Step 컴포넌트 공통 Props
// ============================================================================

/**
 * Step 컴포넌트 Props
 * @description 다양한 폼에서 사용 가능하도록 any 타입 사용
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StepProps = { form: UseFormReturn<any> };
